/**
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════
 * THE ONE VOCABULARY FOR REVIEW-WORKBENCH TELEMETRY
 * ═══════════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Nothing instruments the review workbench today, so the next wave of this programme would be decided
 * by guessing. Four questions have to be answerable from data:
 *
 *   1. **Where do reviewers fall out of the loop?**  opened → committed a blind answer → key revealed →
 *      decided. Each of the four is an event below, and the three transitions between them are the only
 *      places a 1,093-item queue can quietly stop moving.
 *   2. **How long does each stage take?**  Carried as whole SECONDS on the transition events. Never as a
 *      keystroke stream — see the redaction rule below.
 *   3. **How often does each mutation fail?**  One event with a controlled `mutation` name, so «reveal
 *      fails 4% of the time» is a GROUP BY rather than a hand-read of the crash log.
 *   4. **Which evidence surfaces are actually looked at?**  If the machine-verdict panel is never read,
 *      it should be placed better or removed, and both answers are worth having.
 *
 * ── why this is a module and not a table in the page ───────────────────────────────────────────────
 *
 * This codebase has produced FIVE duplicated vocabulary tables (`checkLabels.js` names three of them,
 * `signals.js` is the fourth's repair) and every one cost a round to find. Two of them stated opposite
 * facts about the same failed check; one gave three pages three different counts for one scan. An event
 * vocabulary drifts the same way and worse: a name spelled two ways is two funnels, each missing half
 * its users, and nothing anywhere errors.
 *
 * So the NAME, the moment it fires, the props it may carry and the allowed values of every enumerated
 * prop live here, and the page and the backend both read this file. `reviewEvents.spec.js` asserts on
 * the VALUES rather than on identifiers, because a guard that lists names is a blacklist and a fourth
 * table under a name nobody thought of shipped green past exactly that guard once already.
 *
 * ══ THE REDACTION RULE, AND IT IS THE POINT OF THIS FILE ═══════════════════════════════════════════
 *
 * **No PII. No learner identifiers. No free text a person typed.**
 *
 * Concretely, and each clause is here because the workbench holds the thing it forbids:
 *
 *   • **No prose.** The reviewer's rejection note, the escalation note, a rewritten stem, an option's
 *     text, a server error sentence — none of it may ride on an event. A reason NOTE is the reviewer's
 *     own words about a colleague's work; a server sentence is free text nobody can mask reliably, which
 *     is the reason `AdminContentPipelineController` never relays an alarm's `detail` below its grant.
 *     Reason CODES are a controlled server-owned vocabulary and would be safe, but they are already
 *     recorded on the verdict row and are not duplicated here — see «one answer to one question».
 *   • **No learner.** Nothing on this screen is about a learner, and nothing may become about one. A
 *     learner id arriving through a duplicate-evidence blob or a misconception shortlist is the way
 *     that happens by accident, so props are an ALLOWLIST and unknown keys are dropped.
 *   • **No keystroke timings.** A per-keypress stream is performance data about a named person, and
 *     this codebase has already split work metrics from people metrics twice — on `/analytics/review-ops`
 *     and, this week, on the morning digest. The same line applies: durations are whole seconds per
 *     STAGE, capped, and there is deliberately no event for selecting an option, typing in the note, or
 *     moving between fields.
 *   • **No answer keys and no verdicts-as-data.** `answer_option_key`, `agreed_with_key` and the reason
 *     codes are the MEASUREMENT the blind protocol exists to produce, and `question_review_verdicts`
 *     is their authoritative home. A telemetry copy would be a second, weaker answer to the same
 *     question — the failure the alarms card is built to avoid, arriving from a different direction.
 *     Telemetry says a commitment HAPPENED and how long it took; what was committed is not its business.
 *
 * The rule is enforced, not merely stated: {@link buildReviewEvent} rebuilds every payload from the
 * declared prop list and type, so a key added to a call site tomorrow is DROPPED until somebody declares
 * it here. A blacklist would have to guess the next field; an allowlist does not.
 *
 * ── what is deliberately absent, so the edge is a decision ─────────────────────────────────────────
 *
 *   • `question_id` IS carried. It is a content identifier, not a person, it is needed to compute
 *     per-item drop-out, and `question_review_verdicts` already records which reviewer decided which
 *     item. What the backend must not do is turn the pair (actor, item, seconds) into a per-person
 *     scorecard on an open route — `docs/operations/review-telemetry.md` states the grant.
 *
 *     Its SHAPE was read off the PHP and not guessed. `QuestionAuthoringService::serializeSummary()`
 *     emits `'question_id' => $question->public_id`, and `newPublicId()` builds that as `qst_` plus
 *     twelve lowercase characters — the review queue's own edit call proves it, since it PUTs to
 *     `/question-bank/questions/{question_id}/review-edit`. It is an opaque ASCII TOKEN, never the
 *     bigint primary key, and a numeric `id` kind here would have dropped it out of every event while
 *     every test still passed. That is the third dead read of this shape found by reading the
 *     serializer instead of the docblock.
 *   • There is NO `reviewer_id` prop. The transport is authenticated; the actor is the request's, never
 *     the payload's. A client-supplied identity is both forgeable and a second source of truth.
 *   • There is NO event for picking an option before the commitment. It would be the keystroke stream
 *     under another name, and the drop-out it would measure is already visible as the gap between
 *     `review.item_opened` and `review.answer_committed`.
 */

/** Every event this panel may emit. A name absent from here is not emitted — {@link reviewEventMeta}. */
const EVENTS = [
  {
    name: 'review.item_opened',
    when: 'An item became the card the reviewer is looking at — on arrival, on next/prev, after a verdict '
      + 'advances the queue, and after an undo puts one back.',
    props: { question_id: 'public_id', position: 'count', blind: 'flag' },
  },
  {
    name: 'review.answer_committed',
    when: 'The pre-reveal commitment POST returned 2xx. This is the top of the blind protocol and the '
      + 'stage the queue most plausibly stalls at.',
    // `unsolved` distinguishes «I answered» from «I looked and could not solve it», which is a different
    // and diagnostic outcome. WHICH option was committed is not carried — that is the measurement, and
    // the verdict row owns it.
    props: { question_id: 'public_id', unsolved: 'flag', seconds: 'seconds' },
  },
  {
    name: 'review.key_revealed',
    when: 'The answer key became visible on the current card, by whatever route.',
    // `via` is the whole reason this is separate from `answer_committed`: a rise in `edit` or
    // `arrived_visible` means keys are reaching reviewers AROUND the blind protocol, and that failure is
    // silent by construction — it does not break the screen, it quietly makes the agreement rate
    // meaningless while it keeps being reported.
    props: { question_id: 'public_id', via: 'enum:REVEAL_ROUTES' },
  },
  {
    name: 'review.decision_submitted',
    when: 'A verdict POST returned 2xx.',
    // `decision` is the server's own four-value vocabulary, not free text. `edited` says the reviewer
    // rewrote the item before deciding, which is the difference between a rejection and a repair.
    props: { question_id: 'public_id', decision: 'enum:DECISIONS', seconds: 'seconds', edited: 'flag' },
  },
  {
    name: 'review.decision_undone',
    when: 'A verdict was voided inside its undo window.',
    props: { question_id: 'public_id' },
  },
  {
    name: 'review.mutation_failed',
    when: 'A review mutation did not succeed — a non-2xx, a timeout, or a thrown request.',
    // The HTTP status and nothing else. The server's sentence is free text and is never carried; a
    // status plus a mutation name is what «reveal fails 4% of the time» needs, and it is all it needs.
    props: { mutation: 'enum:MUTATIONS', status: 'count' },
  },
  {
    name: 'review.evidence_shown',
    when: 'An evidence surface was PRESENTED on the current card. Emitted once per item per phase, never '
      + 'per re-render.',
    // `phase` matters because the blind and revealed halves of this screen show different evidence, and
    // an impression counted without it would merge «shown before the commitment» with «shown after».
    props: { question_id: 'public_id', surface: 'enum:SURFACES', phase: 'enum:PHASES' },
  },
  {
    name: 'review.evidence_opened',
    when: 'The reviewer expanded a collapsible surface. Only the surfaces that actually collapse can '
      + 'emit this — see SURFACES for which, and the contract doc for why the distinction is load-bearing.',
    props: { surface: 'enum:SURFACES' },
  },
  {
    name: 'review.session_ended',
    when: 'The workbench is being left — unmount, tab close, refresh, or a hidden page.',
    props: { opened: 'count', committed: 'count', revealed: 'count', decided: 'count', seconds: 'seconds' },
  },
]

/** How the key came to be visible. `commitment` is the protocol; the other two are around it. */
export const REVEAL_ROUTES = Object.freeze(['commitment', 'arrived_visible', 'edit'])

/** The server's verdict vocabulary, spelled exactly as `POST /questions/{id}/review` takes it. */
export const DECISIONS = Object.freeze(['approve', 'reject', 'request_revision', 'cannot_judge'])

/** Every mutation this screen can make, so a failure rate can be read per mutation. */
export const MUTATIONS = Object.freeze([
  'claim',
  'reveal',
  'decide',
  'undo_verdict',
  'review_edit',
  'return_to_review',
])

/**
 * The evidence surfaces, and whether each one can be OPENED or only SHOWN.
 *
 * The brief that asked for this asked «if the machine-verdict panel is never expanded, place it better
 * or remove it» — and on this screen that panel does not collapse: the solver verdict, the solution
 * chain and the two signal blocks are rendered inline whenever they apply. So `review.evidence_opened`
 * is not available for them and pretending otherwise would produce a permanent zero that reads as «never
 * looked at» rather than «never collapsible». What IS measurable for them is presentation, and the
 * decision the brief wants is available a different way: an item where the panel was SHOWN and the
 * reviewer decided in four seconds did not read it.
 */
export const SURFACES = Object.freeze([
  // shown-only — rendered inline, no expander exists
  'pre_reveal_signals',
  'post_reveal_signals',
  'machine_verdict',
  'solution_chain',
  'misconception_evidence',
  'duplicate_evidence',
  'failed_checks',
  // collapsible — these can also emit `review.evidence_opened`
  'queue_metrics',
  'filters',
  'shape',
  'shortcut_help',
])

/** The subset of {@link SURFACES} that has an expander, and may therefore emit `review.evidence_opened`. */
export const COLLAPSIBLE_SURFACES = Object.freeze(['queue_metrics', 'filters', 'shape', 'shortcut_help'])

/** Which half of the blind protocol the card was in. */
export const PHASES = Object.freeze(['blind', 'revealed'])

const ENUMS = Object.freeze({ REVEAL_ROUTES, DECISIONS, MUTATIONS, SURFACES, PHASES })

/**
 * A stage duration is clamped rather than dropped.
 *
 * A reviewer who leaves a tab open over lunch would otherwise contribute a 40,000-second item to every
 * median. Two hours is past any plausible single item and the clamp is visible in the data as a spike at
 * the cap, which is a fact a reader can see rather than a row that silently vanished.
 */
export const MAX_STAGE_SECONDS = 7200

/** @type {ReadonlyArray<{name: string, when: string, props: Readonly<Record<string, string>>}>} */
export const REVIEW_EVENTS = Object.freeze(EVENTS.map((e) => Object.freeze({ ...e, props: Object.freeze({ ...e.props }) })))

/** The names, in the order they occur in a reviewer's loop. The backend validates against this list. */
export const REVIEW_EVENT_NAMES = Object.freeze(REVIEW_EVENTS.map((e) => e.name))

/**
 * The names again, keyed for a call site: `emit(REVIEW_EVENT.ITEM_OPENED, …)`.
 *
 * This exists so that no page ever contains an event name as a literal, which is the property the drift
 * guard actually asserts. It caught the first draft of the workbench instrumentation doing exactly that:
 * eight name strings spelled out in `ReviewQueuePage.vue`, which is a second vocabulary however carefully
 * it is kept in step — and the five that came before it were all kept carefully in step too, right up
 * until they were not.
 *
 * Derived from {@link REVIEW_EVENTS} rather than typed out, so the map cannot itself become the drift:
 * the key is the name with the `review.` prefix stripped and upper-cased, and there is nowhere for a
 * sixth spelling to live.
 *
 * @type {Readonly<Record<string, string>>}
 */
export const REVIEW_EVENT = Object.freeze(Object.fromEntries(
  REVIEW_EVENT_NAMES.map((name) => [name.replace(/^review\./, '').toUpperCase(), name]),
))

const BY_NAME = new Map(REVIEW_EVENTS.map((e) => [e.name, e]))

/**
 * The declaration for one event name, or `null`.
 *
 * Null for an unknown name, and the caller drops the event. That is the fail-closed half of this module:
 * a typo'd or invented name produces NOTHING rather than an untyped payload that reaches the wire and
 * has to be redacted after the fact.
 *
 * @param {unknown} name
 * @returns {{name: string, when: string, props: Record<string, string>}|null}
 */
export function reviewEventMeta(name) {
  return (typeof name === 'string' && BY_NAME.get(name)) || null
}

/**
 * The only free-form string any prop may hold, and it is not free-form.
 *
 * An opaque ASCII token: `qst_a1b2c3d4e5f6` and nothing that is not shaped like one. No whitespace, no
 * `@`, no `.`, no non-ASCII — so a Persian rejection note, a question stem, an email address, a URL and
 * a server sentence are all structurally excluded rather than filtered out afterwards. The 64-character
 * ceiling is five times the identifier this backend mints and still far short of any prose.
 */
// `qst_` plus twelve lowercase alphanumerics — the shape `QuestionAuthoringService::newPublicId()`
// mints, stated in this module's own docblock above and now enforced rather than described. The
// server's twin, `ReviewTelemetryVocabulary::PUBLIC_ID`, carries the identical pattern; a looser
// client is not a leak (the server rebuilds every bag from its own declaration and nulls what does
// not match) but it IS drift, and a rule that two files state differently is a rule one of them will
// eventually lose. The previous `/^[A-Za-z0-9_-]{1,64}$/` was a SHAPE guard that excluded prose,
// addresses and whitespace while admitting any token a caller cared to invent — an audit stored
// `AliKhodabandehlo-1370` through the server's copy of it before that one was tightened.
const PUBLIC_ID = /^qst_[a-z0-9]{12}$/

/** One prop, coerced to its declared kind, or `undefined` when it cannot be — in which case it is omitted. */
function coerce(kind, value) {
  if (kind === 'flag') return typeof value === 'boolean' ? value : undefined

  if (kind === 'public_id') return typeof value === 'string' && PUBLIC_ID.test(value) ? value : undefined

  if (kind === 'count' || kind === 'seconds') {
    const n = typeof value === 'number' ? value : Number(value)
    if (!Number.isFinite(n)) return undefined
    const whole = Math.round(n)
    if (whole < 0) return undefined

    return kind === 'seconds' ? Math.min(whole, MAX_STAGE_SECONDS) : whole
  }

  if (kind.startsWith('enum:')) {
    const allowed = ENUMS[kind.slice(5)]

    return Array.isArray(allowed) && allowed.includes(value) ? value : undefined
  }

  return undefined
}

/**
 * Build the payload for one event, or `null` if the name is unknown.
 *
 * This is where the redaction rule at the top of the file becomes a property of the code rather than a
 * promise in a comment. The result is REBUILT from {@link REVIEW_EVENTS} — every key the declaration
 * does not name is dropped, every value that is not of the declared kind is omitted, and there is no
 * kind that accepts a string outside a named enum. So a call site that starts passing `{ note }`,
 * `{ stem }`, `{ learner_id }` or a server error sentence emits an event WITHOUT them; nothing to
 * redact later, and nothing to notice too late.
 *
 * Omit, never null: a prop that could not be coerced is ABSENT from the payload, in the same way the
 * governance serializer omits `generator_version` below its grant rather than nulling it. A present null
 * asserts a measured absence; an absent key says nothing, which is the truth.
 *
 * @param {unknown} name
 * @param {Record<string, unknown>} [props]
 * @returns {{name: string, props: Record<string, number|boolean|string>}|null}
 */
export function buildReviewEvent(name, props = {}) {
  const meta = reviewEventMeta(name)
  if (!meta) return null

  const source = props && typeof props === 'object' ? props : {}
  const out = {}

  for (const [key, kind] of Object.entries(meta.props)) {
    const value = coerce(kind, source[key])
    if (value !== undefined) out[key] = value
  }

  return { name: meta.name, props: out }
}
